import type { ToastKind } from "./toastContext";

const storageKey = "portal-pending-toast";

interface PendingToast {
  message: string;
  kind: ToastKind;
}

/**
 * Keeps a confirmation across a full page load, for an action that reloads the whole portal (such as
 * switching identity, so that every page re-reads the session). The toast stack is React state and does
 * not survive the reload, so the message waits here until {@link ToastProvider} mounts and shows it.
 * Storage can be blocked, in which case the confirmation is simply not shown.
 */
export function queuePendingToast(message: string, kind: ToastKind): void {
  try {
    const pending: PendingToast = { message, kind };
    window.sessionStorage.setItem(storageKey, JSON.stringify(pending));
  } catch {
    // Storage unavailable: the reload still happens; only the confirmation is lost.
  }
}

/** Returns the queued confirmation once and removes it, or null when none is waiting. */
export function takePendingToast(): PendingToast | null {
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (raw === null) {
      return null;
    }

    window.sessionStorage.removeItem(storageKey);
    return JSON.parse(raw) as PendingToast;
  } catch {
    return null;
  }
}

/** Reloads the portal at its home page, so that the session and every page's data are read again. */
export function reloadPortal(): void {
  window.location.replace("/");
}
